import { useState, useEffect, createContext, useContext } from "react";
import { authAPI } from "../services/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  /**
   * Efecto para cargar usuario al inicio
   */
  useEffect(() => {
    if (token) {
      // Intentar cargar datos del usuario desde localStorage primero
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setLoading(false);

          // Intentar actualizar datos del servidor en background (opcional)
          fetchUserProfile(false);
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          // Fallback to fetching from server
          fetchUserProfile(true);
        }
      } else {
        // No hay datos locales, intentar obtener del servidor
        fetchUserProfile(true);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  /**
   * Obtiene perfil del usuario actual
   * @param {boolean} setLoadingState - Si debe controlar el estado de loading
   */
  const fetchUserProfile = async (setLoadingState = true) => {
    try {
      const userData = await authAPI.getProfile();
      setUser(userData);

      // Guardar en localStorage para próximas sesiones
      localStorage.setItem("user", JSON.stringify(userData));
    } catch (error) {
      console.error("Error fetching user profile:", error);

      // Solo hacer logout si es un error de autenticación real (401/403)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.log("Token inválido o expirado, cerrando sesión...");
        logout();
      } else {
        console.log("Error temporal de red/servidor, manteniendo sesión...");
        // Si hay un token pero falló la obtención del perfil, usar datos básicos si no hay usuario
        const storedToken = localStorage.getItem("token");
        if (storedToken && !user && setLoadingState) {
          const tempUser = {
            id: "temp",
            name: "Usuario",
            email: "temp@temp.com",
            role: "cliente",
            temp: true,
          };
          setUser(tempUser);
          localStorage.setItem("user", JSON.stringify(tempUser));
        }
      }
    } finally {
      if (setLoadingState) {
        setLoading(false);
      }
    }
  };

  const login = async (email, password) => {
    try {
      const result = await authAPI.login(email, password);

      if (result.success) {
        // La respuesta exitosa tiene la estructura: result.data = {token, user}
        const { token: newToken, user: userData } = result.data;

        if (newToken && userData) {
          // Guardar token y usuario en localStorage
          localStorage.setItem("token", newToken);
          localStorage.setItem("user", JSON.stringify(userData));

          // Actualizar estados
          setToken(newToken);
          setUser(userData);
        } else {
          return { success: false, error: "Respuesta de login incompleta" };
        }
      } else {
        console.error("❌ Error en login:", result.error);
      }

      return result;
    } catch (error) {
      return { success: false, error: error.message || "Error inesperado" };
    }
  };

  const register = async (userData) => {
    const result = await authAPI.register(userData);

    if (result.success) {
      const { token: newToken, user: newUser } = result.data;
      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(newUser));
      setToken(newToken);
      setUser(newUser);
    }

    return result;
  };

  const logout = () => {
    console.log("Cerrando sesión...");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth debe ser usado dentro de AuthProvider");
  }

  return context;
};
